import React, { useState } from 'react';
import { Check, ArrowRight, Briefcase } from 'lucide-react';

const Professionals = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const benefits = [
    'Unlimited floor plan analyses',
    'API access for integration',
    'White-label solutions',
    'Priority support',
    'Custom training for your team',
    'Advanced reporting features',
    'Multi-project management',
    'Dedicated account manager',
  ];

  const useCases = [
    {
      title: 'Architecture Firms',
      description: 'Integrate Vastu analysis into your design workflow',
      accent: 'bg-[#C62828]'
    },
    {
      title: 'Real Estate Developers',
      description: 'Ensure Vastu compliance across projects',
      accent: 'bg-[#1565C0]'
    },
    {
      title: 'Interior Designers',
      description: 'Offer Vastu-aligned design solutions',
      accent: 'bg-[#D97706]'
    },
    {
      title: 'Construction Companies',
      description: 'Optimize site planning and layouts',
      accent: 'bg-[#2E7D32]'
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTimeout(() => {
      setSubmitted(true);
    }, 1000);
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#F9F6F0] relative overflow-hidden">
      {/* Background drifting blobs */}
      <div className="bg-blob blob-gold top-[10%] left-[-5%] w-[400px] h-[400px] opacity-35" />
      <div className="bg-blob blob-saffron bottom-[15%] right-[-5%] w-[450px] h-[450px] opacity-40" />

      <div className="container mx-auto max-w-5xl px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FFF3E0] px-4 py-2 rounded-full mb-4">
            <Briefcase className="w-4 h-4 text-[#D97706]" />
            <span className="text-xs font-bold text-[#D97706] uppercase tracking-wider">For Professionals</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold font-vedic text-[#352520] mb-6">
            Vastuspace.AI for Architects & Builders
          </h1>
          <p className="text-sm md:text-base text-[#5D4E37] max-w-2xl mx-auto font-light leading-relaxed">
            Professional-grade Vastu analysis tools designed for architecture firms, developers, and construction companies.
          </p>
        </div>

        {/* Benefits Card */}
        <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 rounded-3xl p-8 md:p-12 mb-16 shadow-sm text-left">
          <h2 className="text-2xl font-bold font-vedic text-[#352520] text-center mb-10">
            Why Choose Vastuspace.AI Pro?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-7 h-7 rounded-full bg-[#FFF3E0] flex items-center justify-center flex-shrink-0 text-[#D97706]">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-sm text-[#5D4E37] font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Perfect For Section */}
        <div className="mb-16 text-left">
          <h2 className="text-2xl font-bold font-vedic text-[#352520] text-center mb-10">Perfect For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className={`w-2.5 h-2.5 rounded-full ${useCase.accent}`} />
                  <h3 className="text-lg font-bold font-vedic text-[#352520]">{useCase.title}</h3>
                </div>
                <p className="text-xs text-[#5D4E37] font-light leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Request Demo Form Container */}
        <div className="bg-white border border-[#EBE6DD]/60 rounded-3xl p-8 md:p-12 shadow-lg max-w-2xl mx-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                <Check className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold font-vedic text-[#352520] mb-2">Request Received!</h3>
              <p className="text-sm text-[#5D4E37] font-light leading-relaxed">
                Our team will contact you within 24 hours to schedule a demo.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold font-vedic text-[#352520] text-center mb-2">Request a Demo</h2>
              <p className="text-xs text-[#5D4E37] text-center mb-8 font-light">
                See how Vastuspace.AI Pro can transform your architectural workflow.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className="px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className="px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company Name"
                    className="px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                  />
                  <div className="relative">
                    <select
                      name="role"
                      required
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-semibold text-[#826239] focus:outline-none focus:border-[#D97706] appearance-none"
                    >
                      <option value="">Select Your Role</option>
                      <option value="architect">Architect</option>
                      <option value="developer">Developer</option>
                      <option value="designer">Interior Designer</option>
                      <option value="builder">Builder/Contractor</option>
                      <option value="other">Other</option>
                    </select>
                    <svg className="w-4 h-4 text-[#826239]/60 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your requirements..."
                  className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706] resize-none"
                ></textarea>

                <button type="submit" className="w-full bg-[#D97706] hover:bg-[#C25E03] text-white py-4 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-md active:scale-97 flex items-center justify-center gap-2">
                  Request Demo
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default Professionals;
